import MP4Box from 'mp4box';
import type { MP4File, MP4ArrayBuffer } from 'mp4box';

export interface MP4BoxVideoInfo {
  mp4boxfile: MP4File;
  videoTrack: any;
  duration: number;
  nbSamples: number;
  width: number;
  height: number;
}

// MP4Box视频解码信息获取
export const getMP4BoxInfo: any = async (data: MP4ArrayBuffer) => {
  return new Promise((resolve, rejcet) => {
    const mp4boxfile = MP4Box.createFile();
    mp4boxfile.onError = function (e) {
      rejcet(e);
    };
    mp4boxfile.onReady = (info) => {
      resolve({ mp4boxfile, info });
    };
    mp4boxfile.appendBuffer(data);
    mp4boxfile.flush();
  });
};

export const getVideoInfoWithWebCodec = async (buffer: MP4ArrayBuffer) => {
  return new Promise<MP4BoxVideoInfo>(async (resolve, reject) => {
    // let buffer = buffer as MP4ArrayBuffer;
    buffer.fileStart = 0;
    const { mp4boxfile, info } = await getMP4BoxInfo(buffer);
    const videoTrack = info.tracks.find((track: any) => track.type === 'video' || track.type === 'metadata');
    if (!videoTrack.video) {
      return reject('视频信息获取失败');
    }
    const timescale = videoTrack.movie_timescale ?? videoTrack.timescale;
    const duration = (videoTrack.movie_duration ?? videoTrack.duration) / timescale;
    const nbSamples = videoTrack.nb_samples; // 视频总帧数
    const width = videoTrack.video.width;
    const height = videoTrack.video.height;
    return resolve({ mp4boxfile, videoTrack, duration, nbSamples, width, height });
  });
};

export const decodeVideo = async (buffer: MP4ArrayBuffer, start: number, end: number, fps: number) => {
  return new Promise<any[]>(async (resolve, reject) => {
    try {
      const { mp4boxfile, videoTrack } = await getVideoInfoWithWebCodec(buffer);
      const chunkList = getChunkList(mp4boxfile, videoTrack.id);
      const videoFrames: any[] = [];
      const chunkListCount = chunkList.length;
      // 时间转换为微秒，适配mp4box的时间单位
      start = start * 1e6;
      end = end * 1e6;
      let outPutFrameCount = Math.floor(((end - start) * fps) / 1e6);
      // 限制输出帧数
      if (outPutFrameCount > chunkListCount) outPutFrameCount = chunkListCount;
      const gapTime = (end - start) / (outPutFrameCount - 1);
      let decoder = initDecoder(videoFrames, outPutFrameCount, mp4boxfile, videoTrack, start, gapTime);
      chunkList.forEach((chunk: EncodedVideoChunk) => decoder.decode(chunk));
      await decoder.flush();
      decoder.close();
      if (videoFrames.length < outPutFrameCount) {
        return reject('解码帧数少于预期帧数');
      }
      resolve(videoFrames);
    } catch (e) {
      reject(e);
    }
  });
};

// 初始化解析器
const initDecoder = (
  videoFrames: any[],
  outPutFrameCount: number,
  mp4boxfile: MP4File,
  videoTrack: any,
  start: number,
  gapTime: number,
) => {
  // const gap = Math.floor(1e6 / fps);
  const decoder = getVideoDecoder(mp4boxfile, videoTrack, videoFrames, outPutFrameCount, start, gapTime);
  return decoder;
};

// 获取帧数组
export const getChunkList = (mp4boxfile: MP4File, videoTrackId: any) => {
  const track = mp4boxfile.getTrackById(videoTrackId);
  const chunkList = track.samples.map((_sample: any, index: number) => {
    const sample = mp4boxfile.getSample(track, index);
    const type = sample.is_sync ? 'key' : 'delta';
    const chunk = new EncodedVideoChunk({
      type,
      timestamp: sample.cts,
      duration: sample.duration,
      data: sample.data,
    });
    return chunk;
  });
  return chunkList;
};

export const getVideoDecoder = (
  mp4boxfile: MP4File,
  videoTrack: any,
  videoFrames: any[],
  outPutFrameCount: number,
  startTime: number,
  gapTime: number,
): VideoDecoder => {
  const decoder = new VideoDecoder({
    output: async (videoFrame) => {
      // important：这里采用设定的帧率抽帧获取数据，而不是获取所有
      if (videoFrames.length < outPutFrameCount && videoFrame.timestamp >= startTime + videoFrames.length * gapTime) {
        createImageBitmap(videoFrame).then((img) => {
          videoFrames.push({
            img,
            duration: videoFrame.duration,
            timestamp: videoFrame.timestamp,
          });
        });
      }
      videoFrame.close();
    },
    error: (err) => {
      console.error('videoDecoder错误:', err);
    },
  });

  // 获取decoder的配置信息
  const getExtradata = () => {
    // 生成VideoDecoder.configure需要的description信息
    const entry = mp4boxfile.moov.traks[0].mdia.minf.stbl.stsd.entries[0];

    const box = entry.avcC ?? entry.hvcC ?? entry.vpcC;
    if (box != null) {
      const stream = new MP4Box.DataStream(undefined, 0, MP4Box.DataStream.BIG_ENDIAN);
      box.write(stream);
      // slice()方法的作用是移除moov box的header信息
      return new Uint8Array(stream.buffer.slice(8));
    }
  };
  const config = {
    codec: videoTrack.codec,
    codedWidth: videoTrack.video.width,
    codedHeight: videoTrack.video.height,
    description: getExtradata(),
  };
  decoder.configure(config);
  return decoder;
};
